import React, { useEffect } from "react";

import { useDrawolaT } from "../i18n";
import { formatMB } from "../quota";

import type { CanWriteResult } from "../types";

type Props = {
  result: Exclude<CanWriteResult, { ok: true }> | null;
  onExport?: () => void;
  onDismiss: () => void;
};

/**
 * Blocking dialog shown when a write would exceed policy or quota.
 * MVP: offers "export this doc" and "dismiss" actions. Not a true blocking
 * modal that prevents tab close — that would require window.beforeunload
 * wiring which is deferred to a follow-up pass.
 */
export const BlockingQuotaModal: React.FC<Props> = ({
  result,
  onExport,
  onDismiss,
}) => {
  const t = useDrawolaT();

  useEffect(() => {
    if (!result) {
      return;
    }
    const handler = (e: BeforeUnloadEvent) => {
      // Soft warning: modern browsers ignore custom text but still prompt.
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [result]);

  if (!result) {
    return null;
  }

  const title = (() => {
    switch (result.reason) {
      case "doc-count":
        return t("quota.title.docCount");
      case "doc-size":
        return t("quota.title.docSize");
      case "policy-total":
        return t("quota.title.policyTotal");
      case "quota":
      default:
        return t("quota.title.quota");
    }
  })();

  const detail = (() => {
    switch (result.reason) {
      case "doc-count":
        return t("quota.detail.docCount", { max: result.meta.max ?? "" });
      case "doc-size":
        return t("quota.detail.docSize", {
          size: formatMB(result.meta.size ?? 0),
        });
      case "policy-total":
        return t("quota.detail.policyTotal");
      case "quota":
      default:
        return t("quota.detail.quota", {
          quota: formatMB(result.meta.quota ?? 0),
          usage: formatMB(result.meta.usage ?? 0),
        });
    }
  })();

  return (
    <div className="drawOla-quota-modal" role="dialog" aria-modal="true">
      <div className="drawOla-quota-modal__panel">
        <h3 className="drawOla-quota-modal__title">{title}</h3>
        <p className="drawOla-quota-modal__body">{detail}</p>
        <p className="drawOla-quota-modal__body">{t("quota.suggestion")}</p>
        <div className="drawOla-quota-modal__actions">
          {onExport && (
            <button
              type="button"
              className="drawOla-quota-modal__button drawOla-quota-modal__button--secondary"
              onClick={onExport}
            >
              {t("quota.exportBtn")}
            </button>
          )}
          <button
            type="button"
            className="drawOla-quota-modal__button drawOla-quota-modal__button--primary"
            onClick={onDismiss}
          >
            {t("quota.ackBtn")}
          </button>
        </div>
      </div>
    </div>
  );
};
